import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { EditOutlined, FileTextOutlined, VerticalAlignMiddleOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Empty, Field, Select, Skeleton, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useGhiPhien } from '../hooks/useBienBan';
import { dinhDangThoiLuong, gomLuotNoi, nenTuCuon, nhanNguoiNoi } from '../nghiep-vu/bien-ban';
import type { Doan, NguoiNoi } from '../types';
import { BadgeNguoiNoi, mauCua, useTenNguoiNoi } from './nhan-nguoi-noi';

/** Cảnh báo độ tin cậy: mức cao thì không hiện gì (ADR-0126, bẫy thứ hai ở phần Bối cảnh). */
export function CanhBaoDoTinCay({ muc, lyDo }: { muc: string | null; lyDo: string | null }) {
  const { t } = useT();
  if (muc !== 'medium' && muc !== 'low') return null;
  return (
    <div role="status" className="flex items-start gap-2.5 rounded bg-warning-50 px-3 py-2.5">
      <WarningOutlined aria-hidden className="mt-0.5 text-warning-600" />
      <div className="min-w-0 text-sm text-warning-700">
        <p className="font-medium">{t('bienBan.loi.canhBao')}</p>
        {lyDo && <p className="mt-0.5 text-xs">{lyDo}</p>}
      </div>
    </div>
  );
}

function FormSuaDoan({
  phienId, doan, nguoiChon, tenCua, onXong,
}: {
  phienId: string;
  doan: Doan;
  nguoiChon: string[];
  tenCua: (label: string | null) => string;
  onXong: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const { suaDoan } = useGhiPhien();
  const [chu, setChu] = useState(doan.text);
  const [nguoi, setNguoi] = useState<string | null>(doan.speaker);
  const [daThu, setDaThu] = useState(false);
  const loiServer = loiTheoTruong(suaDoan.error);
  const loiChung = suaDoan.error && !loiServer['/text'] && !loiServer['/speaker'];

  const luu = () => {
    setDaThu(true);
    if (!chu.trim()) return;
    const vao = {
      ...(chu.trim() !== doan.text ? { text: chu.trim() } : {}),
      ...(nguoi && nguoi !== doan.speaker ? { speaker: nguoi } : {}),
    };
    if (Object.keys(vao).length === 0) {
      onXong();
      return;
    }
    suaDoan.mutate({ id: phienId, doanId: doan.id, vao }, {
      onSuccess: () => {
        toast.success(t('bienBan.loi.daLuuDoan'));
        onXong();
      },
    });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    // Lọt ra ngoài thì phím tắt của trình phát ăn mất phím đang gõ.
    e.stopPropagation();
    if (e.key === 'Escape') onXong();
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) luu();
  };

  return (
    <div className="space-y-3 rounded border border-strong bg-surface p-3" onKeyDown={onKeyDown}>
      <Field label={t('bienBan.loi.nhanNoiDung')} error={loiServer['/text'] ?? (daThu && !chu.trim() ? t('bienBan.loi.loiTrong') : undefined)}>
        {(p) => <Textarea {...p} autoFocus autoSize={{ minRows: 2, maxRows: 10 }} value={chu} onChange={(e) => setChu(e.target.value)} />}
      </Field>
      <Field label={t('bienBan.loi.nhanNguoiNoi')} error={loiServer['/speaker']}>
        {(p) => (
          <Select<string>
            {...p}
            className="w-full sm:w-64"
            value={nguoi ?? undefined}
            placeholder={t('bienBan.loi.chuaRo')}
            onChange={setNguoi}
            options={nguoiChon.map((l) => ({ value: l, label: tenCua(l) }))}
          />
        )}
      </Field>
      {loiChung && <LoiTrongHop>{dichLoi(suaDoan.error)}</LoiTrongHop>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onXong}>{t('chung.huy')}</Button>
        <Button variant="primary" size="sm" loading={suaDoan.isPending} onClick={luu}>{t('bienBan.loi.luuDoan')}</Button>
      </div>
    </div>
  );
}

const DongDoan = memo(function DongDoan({
  doan, dangPhat, coSua, onTua, onSua,
}: {
  doan: Doan;
  dangPhat: boolean;
  coSua: boolean;
  onTua: (giay: number) => void;
  onSua: (id: string) => void;
}) {
  const { t } = useT();
  const tg = dinhDangThoiLuong(doan.start) ?? '';
  return (
    <li
      data-doan={doan.id}
      aria-current={dangPhat ? 'true' : undefined}
      className={cn('flex items-start gap-2 rounded px-2 py-1.5 transition-colors', dangPhat && 'bg-primary-50')}
    >
      <button
        type="button"
        data-moc
        onClick={() => onTua(doan.start)}
        aria-label={t('bienBan.loi.tuaToi', { tg })}
        title={t('bienBan.loi.tuaToi', { tg })}
        className={cn(
          'mt-0.5 shrink-0 rounded px-1 font-mono text-xs tabular-nums text-content-muted transition-colors',
          'hover:bg-subtle hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
          dangPhat && 'font-semibold text-primary-700',
        )}
      >
        {tg}
      </button>
      <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-content-primary">
        {doan.text}
        {doan.edited && <span className="ml-1.5 text-2xs text-content-muted">({t('bienBan.loi.daSua')})</span>}
      </p>
      {coSua && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-content-muted"
          aria-label={t('bienBan.loi.suaDoan', { tg })}
          title={t('bienBan.loi.suaDoan', { tg })}
          icon={<EditOutlined />}
          onClick={() => onSua(doan.id)}
        />
      )}
    </li>
  );
});

const giamChuyenDong = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Bản chép lời theo lượt nói. Bấm mốc thì tua; đoạn đang phát được làm nổi và tự cuộn tới.
 *
 * **Tôn trọng người đang cuộn tay:** lăn chuột, vuốt, kéo thanh cuộn hay phím cuộn đều tạm dừng tự cuộn trong 8 giây và hiện
 * nút quay về. Bắt các sự kiện đó chứ không bắt `scroll`, vì `scroll` cũng nổ khi chính màn tự cuộn. Đang sửa đoạn thì không
 * tự cuộn, kẻo ô đang gõ trôi đi.
 *
 * Phím: mũi tên lên, xuống đi giữa các mốc thời gian; Tab đi qua mốc và nút sửa như thường.
 */
export function BanChepLoi({
  phienId, ds, speakers, idDangPhat, coSua, onTua,
}: {
  phienId: string;
  ds: readonly Doan[];
  speakers: readonly NguoiNoi[];
  idDangPhat: string | null;
  coSua: boolean;
  onTua: (giay: number) => void;
}) {
  const { t } = useT();
  const tenCua = useTenNguoiNoi(speakers);
  const luot = useMemo(() => gomLuotNoi(ds), [ds]);
  const nguoiChon = useMemo(() => nhanNguoiNoi(speakers, [...ds]), [speakers, ds]);
  const [dangSua, setDangSua] = useState<string | null>(null);
  const [tamDung, setTamDung] = useState(false);
  const khung = useRef<HTMLDivElement>(null);
  const lanCuonTay = useRef<number | null>(null);

  const cuonToi = (id: string) => {
    const k = khung.current;
    const dong = k?.querySelector<HTMLElement>(`[data-doan="${CSS.escape(id)}"]`);
    if (!k || !dong) return;
    const hkhung = k.getBoundingClientRect();
    const hdong = dong.getBoundingClientRect();
    const tren = k.scrollTop + hdong.top - hkhung.top - k.clientHeight / 3;
    k.scrollTo({ top: tren, behavior: giamChuyenDong() ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    if (!idDangPhat || dangSua) return;
    if (!nenTuCuon(Date.now(), lanCuonTay.current)) return;
    lanCuonTay.current = null;
    setTamDung(false);
    cuonToi(idDangPhat);
  }, [idDangPhat, dangSua]);

  const cuonTay = () => {
    lanCuonTay.current = Date.now();
    setTamDung(true);
  };

  const veDoanDangPhat = () => {
    lanCuonTay.current = null;
    setTamDung(false);
    if (idDangPhat) cuonToi(idDangPhat);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (['PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) cuonTay();
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const hien = (e.target as HTMLElement).closest('[data-moc]');
    if (!hien) {
      cuonTay();
      return;
    }
    const cacMoc = [...(khung.current?.querySelectorAll<HTMLElement>('[data-moc]') ?? [])];
    const toi = cacMoc[cacMoc.indexOf(hien as HTMLElement) + (e.key === 'ArrowDown' ? 1 : -1)];
    if (toi) {
      e.preventDefault();
      toi.focus();
    }
  };

  return (
    <div className="relative">
      <div
        ref={khung}
        role="region"
        aria-label={t('bienBan.loi.vungCuon')}
        className="max-h-[62vh] space-y-3 overflow-y-auto pr-1"
        onWheel={cuonTay}
        onTouchMove={cuonTay}
        onPointerDown={(e) => {
          // Bấm đúng vào khung (không vào con) là bấm vào thanh cuộn.
          if (e.target === e.currentTarget) cuonTay();
        }}
        onKeyDown={onKeyDown}
      >
        {luot.map((l) => (
          <section key={l.khoa} aria-label={tenCua(l.speaker)} className={cn('border-l-2 pl-2', l.speaker ? mauCua(l.speaker).vien : 'border-strong')}>
            <div className="mb-0.5 flex flex-wrap items-center gap-2 px-2">
              <BadgeNguoiNoi label={l.speaker} ten={tenCua(l.speaker)} />
            </div>
            <ul>
              {l.doan.map((d) =>
                dangSua === d.id ? (
                  <li key={d.id} data-doan={d.id} className="py-1">
                    <FormSuaDoan phienId={phienId} doan={d} nguoiChon={nguoiChon} tenCua={tenCua} onXong={() => setDangSua(null)} />
                  </li>
                ) : (
                  <DongDoan key={d.id} doan={d} dangPhat={d.id === idDangPhat} coSua={coSua} onTua={onTua} onSua={setDangSua} />
                ),
              )}
            </ul>
          </section>
        ))}
      </div>
      {tamDung && idDangPhat && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
          <Button className="pointer-events-auto shadow-sm" variant="secondary" size="sm" icon={<VerticalAlignMiddleOutlined />} onClick={veDoanDangPhat}>
            {t('bienBan.loi.veDoanDangPhat')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Khung tải, lỗi, rỗng của vùng bản chép lời. */
export function KhungBanChepLoi({
  dangTai, loi, onThuLai, rong, children,
}: {
  dangTai: boolean;
  loi: string | null;
  onThuLai: () => void;
  rong: string | null;
  children: ReactNode;
}) {
  const { t } = useT();
  if (dangTai) {
    return (
      <div aria-busy className="space-y-3">
        {['w-11/12', 'w-3/4', 'w-5/6', 'w-2/3'].map((rong) => <Skeleton key={rong} className={cn('h-4', rong)} />)}
      </div>
    );
  }
  if (loi) {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 py-2">
        <span className="min-w-0 flex-1 basis-60 text-sm text-danger-700">{loi}</span>
        <Button variant="outline" size="sm" onClick={onThuLai}>{t('chung.thuLai')}</Button>
      </div>
    );
  }
  if (rong) return <Empty icon={FileTextOutlined} title={rong} />;
  return <>{children}</>;
}
